import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import { mediaCache } from '../../service/mediaCache';
import { likeLocalMedia } from './activ8Reducer';
import timeout from '../../utils/timeout';
import { ACTIV8_TABS } from '../../constants';
import { updateBucketItem } from '../ai-studio/aiStudioReducer';
import { removeBucketItem } from '../ai-studio/aiStudioActions';

const FIND_ACTIV8_CONTENT = 'activ8/FIND_ACTIV8_CONTENT';
const GET_ACTIV8_TAGS = 'activ8/FIND_ACTIV8_TAGS';
const GET_ACTIV8_CONTENT_USERS = 'activ8/GET_ACTIV8_CONTENT_USERS';
const GET_ACTIV8_PROJECT_NAME = 'activ8/GET_ACTIV8_PROJECT_NAME';
const GET_ACTIV8_DUPLICATES = 'activ8/GET_ACTIV8_DUPLICATES';
const LIKE_ACTIV8_CONTENT = 'activ8/LIKE_ACTIV8_CONTENT';
const DOWNLOAD_SOURCE = 'activ8/DOWNLOAD_SOURCE';
const DELETE_ACTIV8_CONTENT = 'activ8/DELETE_ACTIV8_CONTENT';
const EDIT_TAGS = 'activ8/EDIT_TAGS';
const UPDATE_CONTENT_BY_ID = 'activ8/UPDATE_CONTENT_BY_ID';

export const MIN_SEARCH_QUERY_LENGTH = 3;

export const getActiv8ProjectName = createAsyncThunk(GET_ACTIV8_PROJECT_NAME, async ({ id }) => {
  const response = await sendRequest(() => callApi.get(`/api/project/project-name/${id}`));
  return response.data;
});

export const findActiv8Content = createAsyncThunk(
  FIND_ACTIV8_CONTENT,
  async ({ id }, { rejectWithValue, getState }) => {
    const { page, size, searchQuery, filters, sort } = getState().activ8.tableSettings;

    if (searchQuery?.length && searchQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return rejectWithValue('Search query must be at least 3 characters long');
    }

    const response = await sendRequest(
      () =>
        callApi.post(`/api/activ8/media/search/content?projectId=${id}&page=${page + 1}&size=${size}&sort=${sort}`, {
          ...(searchQuery ? { query: searchQuery } : {}),
          ...(filters.reaction?.length ? { reaction: filters.reaction[0].value } : {}),
          ...(filters.mediaType?.length ? { mediaType: filters.mediaType[0].value } : {}),
          ...(filters.tags?.length ? { tags: filters.tags.map((tag) => tag.value) } : {}),
          ...(filters.negativeQuery?.length ? { negativeQuery: filters.negativeQuery[0] } : {}),
          ...(filters.username?.length ? { username: filters.username[0].value } : {}),
        }),
      {
        onError: (e) => {
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);

export const findActiv8Tags = createAsyncThunk(GET_ACTIV8_TAGS, async ({ id, query }, { rejectWithValue }) => {
  const response = await sendRequest(
    () => callApi.post(`/api/activ8/media/search/tags?projectId=${id}&query=${query}&page=${1}&size=${999}`),
    {
      onError: (e) => {
        return rejectWithValue(e);
      },
    },
  );

  return response.data;
});

export const getActiv8ContentUsers = createAsyncThunk(GET_ACTIV8_CONTENT_USERS, async ({ id }, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/activ8/media/users?projectId=${id}`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  return response.data;
});

export const getDuplicates = createAsyncThunk(
  GET_ACTIV8_DUPLICATES,
  async ({ projectId, media }, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append('media', media, media.name);

    const response = await sendRequest(
      () =>
        callApi.post(`/api/activ8/media/pre/upload?projectId=${projectId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      {
        onError: (e) => {
          return rejectWithValue(e);
        },
      },
    );
    return response.data;
  },
);

export const likeActiv8Content = createAsyncThunk(
  LIKE_ACTIV8_CONTENT,
  async ({ id, reaction }, { dispatch, rejectWithValue }) => {
    dispatch(likeLocalMedia({ id, reaction }));

    await sendRequest(() =>
      callApi.post(`/api/activ8/media/react?contentId=${id}&reaction=${reaction}`, {
        onError: (e) => {
          return rejectWithValue(e);
        },
      }),
    );

    return id;
  },
);

export const downloadSource = createAsyncThunk(DOWNLOAD_SOURCE, async ({ id }, { rejectWithValue }) => {
  const response = await sendRequest(() =>
    callApi.get(`/api/activ8/media/download?contentId=${id}`, { responseType: 'arraybuffer' }),
  );

  const fileName = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '');

  const fileUrl = URL.createObjectURL(new Blob([response.data]));
  return { file: fileUrl, fileName };
});

export const deleteActiv8Content = createAsyncThunk(
  DELETE_ACTIV8_CONTENT,
  async ({ contentId, projectId }, { rejectWithValue, dispatch, getState }) => {
    await sendRequest(() => callApi.delete(`/api/activ8/media?contentId=${contentId}`), {
      onError: (e) => rejectWithValue(e),
    });

    const cachedUrl = mediaCache.get(contentId);
    if (cachedUrl) {
      URL.revokeObjectURL(cachedUrl);
      mediaCache.delete(contentId);
    }

    const activ8Tab = getState().activ8.activ8Tab;

    if (activ8Tab === ACTIV8_TABS.FIND) {
      const affectedBucket = getState().activ8.content.find((item) =>
        item.media.some((mediaItem) => mediaItem.id === contentId),
      );

      if (!affectedBucket) {
        return rejectWithValue('Content not found');
      }

      const updatedBucket = {
        ...affectedBucket,
        media: affectedBucket.media.filter((mediaItem) => mediaItem.id !== contentId),
      };

      if (updatedBucket.media.length === 0) {
        await timeout(300);
        dispatch(findActiv8Content({ id: projectId }));
      }

      return { updatedBucket };
    }

    if (activ8Tab === ACTIV8_TABS.STUDIO) {
      await dispatch(removeBucketItem({ id: contentId }));
      return { updatedBucket: null };
    }

    return rejectWithValue('Content not found');
  },
);

export const editTags = createAsyncThunk(EDIT_TAGS, async ({ contentId, tags }, { dispatch, getState }) => {
  await timeout(1000);
  // const response = await sendRequest(() => callApi.post(`/api/activ8/media/edit/tags?contentId=${id}`, { tags }));
  const activ8Tab = getState().activ8.activ8Tab;

  if (activ8Tab === ACTIV8_TABS.STUDIO) {
    dispatch(updateBucketItem({ id: contentId, newContent: { tags } }));
  }

  return { contentId, tags };
});

export const updateContentById = createAsyncThunk(
  UPDATE_CONTENT_BY_ID,
  async ({ contentId }, { rejectWithValue, dispatch, getState }) => {
    const response = await sendRequest(() => callApi.get(`/api/activ8/media/find/by/id?contentId=${contentId}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });
    const activ8Tab = getState().activ8.activ8Tab;

    if (activ8Tab === ACTIV8_TABS.STUDIO) {
      dispatch(updateBucketItem({ id: contentId, newContent: { ...response.data } }));
    }

    return response.data;
  },
);
